// Phase 26 — shared agenda-event row for CaseView and CollectiveCaseView.
//
// Replaces the two near-duplicate inline event-card blocks. Renders one event
// card plus (when expanded) its EventSubtasks panel. Behaviour is identical in
// both views; per-view wiring (store association, deletion semantics) is passed
// in as props.
//
// Deliverable A (parity) — this single component levels the previous CV/CCV
// drift up to the fuller (CaseView) behaviour everywhere:
//   * location icon (locIcon) beside the location label,
//   * event docLink rendering,
//   * confirmAndUndo delete with linked-deadline (dlEventId) cleanup,
//   * unified edit/delete permission gate (canEditAgenda OR owner/assignee).
//
// Deliverable B (inline edit) — in the UNLOCKED state, and only for events the
// actor may edit, name / date / Level (priority) become click-to-edit using the
// same control vocabulary as the canonical "ag" modal. Edits persist through the
// same write path the modal uses (s(SK.ag, …)). The Edit pencil is retained for
// add + full edit.

import { Fragment, useState, useEffect } from "react";
import type { ReactNode } from "react";
// BRIEF 105 §3a — `File`, not `FileText`. See CaseTaskRow.tsx for the census.
import { Pencil, Archive, File, Check } from "lucide-react";
import { I, evDayClr, pname, scrollToStage, provisionalLevel } from "../utils";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers"; // 109 §7f
import { isDividerCode } from "../utils/taxonomyMap"; // 110 §3a
import { JoinBtn } from "./SharedUI";
import { placeOf } from "../lib/placeName"; // 216 addendum 4 §8 (3)
import { choosableCo } from "../lib/collaboratorState";
import { useTranslation } from "../i18n";
import SubtaskCounter, {
  EventSubtasks,
  canWriteEventSubtasks,
} from "./SubtaskCounter";
import { RowTimerBlock } from "./RowTimerBlock"; // 116 §3
import { usePermissions } from "../hooks/usePermissions";
// BRIEF 102 §2f — the day counter this file and AgendaView had each written.
import { dayDiff, dayCountdownLabel } from "../lib/dayCountdown";
import EventDayMark from "./EventDayMark"; // 136 §3
import { eventStatusOf } from "../lib/eventStatus"; // 136 §3
import { useTaxonomy } from "../hooks/useTaxonomy"; // 136 §3
import { RAD, FS, DIMMED_ROW_OPACITY } from "../lib/tokens"; // 195 §A.4 — the one dimming
// BRIEF 95 §3a(a) via §3b — one date control, DD/MM/YY at rest, ISO on the wire.
import DateField from "./DateField";
// BRIEF 95 §3b — the documents pane the task row has had since C72.
import TaskDocsPane from "./TaskDocsPane";
// BRIEF 101 §1 — the date block, extracted; this file was one of its three copies.
import DateBlock from "./DateBlock";
// BRIEF 105 §3b — the third drawer.
import NotesDrawer, { hasNoteText } from "./NotesDrawer"; // 118 §9
// BRIEF 107 §2b — the shared drawer icons; see RowDrawerToggle.tsx.
import {
  NotesToggle,
  DocsToggle,
  docsToggleVisible,
} from "./RowDrawerToggle";
import LinkIcon from "./LinkIcon"; // 111 §5c
import { BADGE_AREA_W } from "../lib/badgeStack"; // 118 §7
import TypeTagBox from "./TypeTagBox"; // 118 §8
import MenuSelect from "./MenuSelect"; // 154B §2
import MatterChip from "./MatterChip"; // 216 addendum 6 §10 (2) — the matter pill, one definition

/* BRIEF 95 §3b — the two shapes this file's own additions read. Written out
   rather than reached for with `any`: `ratchets.js` may only ever shrink, and a
   new `any` in new code is exactly what it is counting. */
type DocRow = { id?: number; archived?: boolean } | null;
type EvRow = { id?: number; [k: string]: unknown };

/* BRIEF 118 §7 — the title line's own gap, named because a SECOND line now has
   to reproduce it exactly. It was an inline `gap: 7` at :324 and nothing else
   could cite it. */
const TITLE_GAP = 7;
/* 216 addendum 4 §8 (3) [RULED 79] — the room between the title line and the
   meta line: the ringed badge (22 px + 1.5 px ring) overhangs the title line's
   text box by about 4 px, so this is what keeps the ring off the place. */
const EVENT_ROWS_GAP = 5;

export default function CaseEventRow({
  ev,
  co = [],
  currentUser,
  todayStr,
  // taxonomy / helpers
  priMap = {},
  itemLevelList = [],
  evtTypes = [],
  etTag,
  /* BRIEF 139 §1 — the longest code `evtTypes` can show, folded ONCE in App
     (`longestTaxonomyCode`) and handed down the path `etTag` already travels.
     This row does not derive it: a max recomputed per row is a max recomputed
     fifty times a pane. */
  evtLongestCode = "",
  coAvs,
  locIcon,
  MultiSelect,
  // store
  ag = [],
  sAg,
  s,
  SK,
  // expansion state
  evExpanded,
  toggleEvExpanded,
  // permission / lock
  agLocked,
  canEditAgenda,
  canEditOthersTasks,
  // handlers
  openModal, // om / globalOm
  /* BRIEF 91 §3a — the event drawer's timer column is built from three things:
     these two and `openModal` above, which this row already had. They are the
     same two `CaseTaskRow` takes from the same hosts (CaseTasksSection passes
     them beside this component's own section). */
  activeTimers,
  startTimer,
  onDeleteEvent, // (ev) => void  — view-specific confirmAndUndo + dlEventId
  /* Phase 29 Stage-3 C25/C26 — compact-column variant: renders ONLY
     [type tag · subtask counter · title · time · description], no date
     block / day label / priority / assignees / location / docLink. Pass
     agLocked=true alongside it so inline editors and the edit/archive
     buttons stay off. onNavEvent: title click navigates the MAIN window
     to the event in context (Dashboard row-click semantics). */
  reduced = false,
  onNavEvent,
  /* BRIEF 98 §4 — the matter this event belongs to, for the OneDrive picker.
     Same shape and same reason as `CaseTaskRow`'s: §4b measured the claim and
     found an observation, not a constraint. Absent = no button, which is how
     the compact-column mount stays as it is. */
  oneDriveMatter,
  /* 109 §7f — the matter's stages + the case_stage entries, for the link
     pill. OPTIONAL: only the CV/CCV agenda sections thread them; the compact
     column passes neither and renders no pill (the brief's scope). */
  stages,
  caseStageEntries,
  // 110 §1h — the open-all/close-all {seq, open} broadcast.
  drawersCast,
  /* 111 §5c — the matter row holding itemLinks; optional (compact column
     passes none → no icon). */
  matterRow,
  /* BRIEF 115 §2 — the Dashboard mounts this card (B.3: one renderer), and
     the three props below are its union contribution — the same three §1 gave
     the task row, plus the chip that is this pane's own.
     onRowNav: the whole-CARD navigate-and-flash click (the host builds it
     from its own _navCase + queueScrollToEv). Distinct from the reduced
     variant's onNavEvent (a title affordance); here the card is the target
     and every control on it stops propagation. Absent = no handler, no
     cursor — CV/CCV unchanged. */
  onRowNav,
  /* 115 §2 — the 110 §1b view-only regime: subtasks read-only, docs quiet,
     notes quiet, and the counter's empty-state add affordance withheld (an
     add affordance opening a drawer that refuses adds is a lie). agLocked
     cannot say this: the padlock gates the CARD's structure and deliberately
     not the note (109 §1d) or the subtask panel's own gate. */
  drawersReadOnly = false,
  /* 216 addendum 7 §11 (2) [RULED 84] — THE DASHBOARD'S CARD (Laurent, 16/10, his 4): the counter leaves the title
     row for a column of its own, centred between the two rows, the case pill ending the second row beside it; and
     the three drawers open INSIDE the card's content. The Dashboard alone passes it — a matter's events passed his
     glance and keep their rows, their counter at the title's end and their drawers attached under the card. */
  dashboardCard = false,
  /* 115 §2 — the matter chip, on the meta line: the Dashboard's cards name
     the case they belong to (CV/CCV cards are inside it, so they never
     could). {label, title?, onClick} — the host owns the resolution and the
     jump; this card owns the rendering, so the chip cannot drift from the
     card it sits on. Absent = nothing. */
  matterChip,
  /* 195 §A [RULED — Laurent, 2026-09-18: "put into archive the exact same
     items as in CV/CCV (the FULL CARDS of events/Tasks), so just reuse what
     exists … make them dimmed though so nobody confuses them with active
     tasks/events"] — THE ARCHIVED MODE, widening the read-only idiom this
     card already carries (`reduced` + agLocked, the note above). The card
     renders as in the matter — date block, type, assignees, counter, level,
     title, the two drawer icons, the day mark, the meta line — DIMMED by the
     one definition (DIMMED_ROW_OPACITY), and every control is INERT: no inline
     editor, no timer, no drawer opens, no join link, no document anchor, no
     card click; the row's only two actions are the ones the host supplies
     through `archivedActions` (the Archive view's Restaurer and Supprimer
     définitivement), rendered where the pencil and the archive button stood.
     Every other prop keeps its meaning; a host that passes neither renders
     byte-identically. */
  archived = false,
  archivedActions,
  /* 209 §27 — the hourglass view's event row, the reduced one extended as the
     task row is: `lead` (a timed event's elapsed, in place of its start-timer and
     record-time icons, first on the line) and `caseLabel` (the case's name and
     number, on the meta line beside the event's hour). Absent everywhere else. */
  lead,
  caseLabel,
}: any) {
  const { t } = useTranslation();
  // 209 §27 — the case's name and number, as the column hands it (a string or nothing)
  const caseLine = typeof caseLabel === "string" ? caseLabel : "";
  const { viewer, can: furCan } = usePermissions();
  /* 190 §F.2 (ruling 190 (4); glance round 2 [D] — RULED: ALL of them) — THE
     FURNITURE KEYS, `furniture.event.<piece>`, gated as `can(k)` with the
     task row's one deviation (CaseTaskRow.tsx `fur`): the load window keeps
     every piece, since `can()` answers false for every key until the
     permissions payload lands and a card that flashed empty would be the
     defect. Chrome only — no route reads a furniture key.
     191 (1) — an UNWRITTEN switch means SHOWN, resolved on the server once
     (sectionRegistry.js `shownUnwritten` → fillUnwrittenDefaults(), shipped
     by /permissions/me as an explicit `true`); 191 (2) — the date block, the
     title and the actions carry NO key: never switchable. */
  const fur = (k: string): boolean => viewer === null || furCan(k);
  /* BRIEF 139 §1 — THE MIRROR, built from the SAME `etTag` the visible chip is
     built from, which is 118 §7's own sentence about the mirror one screen
     over: "so the mirror cannot drift from what it mirrors". Once per render,
     not once per site — this row renders the box twice (the title line, and
     §7's hidden run on the Dashboard) and they must be the same box.
     Typed on arrival although the props object is `any` (rule 4). Null when the
     taxonomy is empty, which is the only way the box has nothing to mirror. */
  const _longestCode: string = evtLongestCode || "";
  /* 157A §8 (direction corrected) — `reduced` travels into the mirror as well
     as the visible chip. In the popout the chip renders at `.colTag`'s metrics
     (the Minuteur chip is the model the three sections agree on), and a mirror
     still built at the old ones would size this box from a chip that is no
     longer on screen — the exact drift 118 §7's sentence above forbids. */
  const _typeMirror: ReactNode = _longestCode
    ? etTag(_longestCode, reduced)
    : null;
  const [edit, setEdit] = useState<any>(null); // null | 'title' | 'date' | 'priority'
  // Empty-state add affordance for the subtask counter: only for a viewer who
  // can actually create an event subtask (same gate as the EventSubtasks panel).
  const subtaskCanWrite = canWriteEventSubtasks(viewer, ev);
  /* BRIEF 95 §3b — THE DOCUMENTS PANE, WHICH THIS ROW NEVER HAD.
     `CaseTaskRow` has carried it since C72 and this row carried nothing, so an
     event's documents existed only inside the Event modal. It gets §3a's result
     whole: the same toggle on the title line, the same drawer, the same pane —
     `eventId` where tasks pass `taskId`, which `TaskDocsPane` has supported as a
     first-class mode since brief 87 §4.
     THE WRITE GATE IS THE SERVER'S, expressed with what this row already holds.
     `routes/agendaEventDocs.js:71` admits admin ∪ the row's assignee union ∪
     `caseView.agenda.edit`; `canWriteEventSubtasks` IS the first two (it is the
     client's reading of the same `rowAssigneeUnion` the route calls) and
     `canEditAgenda` is the third. Deriving a fourth predicate here is how the
     client comes to disagree with the route about who may write.
     NOT gated on `agLocked`: the padlock governs INLINE EDITING of the card, and
     the task row's documents toggle is not gated on `tkLocked` either. */
  const docsCanTouch = subtaskCanWrite || !!canEditAgenda;
  /* BRIEF 117 §7 — the two drawers' own ADD predicates, once. The paragraph
     above stays true — `docsCanTouch` is the rights question and is still not
     lock-gated, because READING a linked document never was. These are the
     narrower question the affordances ask: can this drawer be added to?
     Subtasks: `writable && !locked && !readOnly` (SubtaskChecklist.tsx:662).
     Documents: `canWrite && !locked` (TaskDocsPane.tsx:364), with `readOnly`
     folded into `canWrite` at the mounts below. */
  /* 195 §A — the archived mode is the padlock AND the view-only regime at
     once, with the note and the drawers inert on top (below). */
  const locked = Boolean(agLocked) || archived === true;
  const readOnly = Boolean(drawersReadOnly) || archived === true;
  /* the chip, read ONCE through a typed local (the seam) */
  /* 216 addendum 4 §8 (4) [RULED 79] — `atRight`: the Dashboard's chip sits at the
     right end of the second row (« ADD the Case Name in 2nd row sticking to the
     right »); the Archive's keeps 118 §7's place at the timer's x. */
  const chip = (matterChip ?? null) as { label: string; title?: string; onClick?: () => void; full?: boolean; atRight?: boolean } | null;
  const subtaskCanAdd = subtaskCanWrite && !locked && !readOnly;
  const docsCanAdd = docsCanTouch && !locked && !readOnly;
  // C72 — archived docs are listed by the pane, dimmed, but do not count.
  // 216 addendum 4 §8 (3) — the place as the line shows it, and the whole for its hover
  const place = placeOf(ev as { roomName?: string | null; location?: string | null });
  const liveDocs: DocRow[] = Array.isArray(ev.docs)
    ? (ev.docs as DocRow[]).filter((d) => d && !d.archived)
    : [];
  const [docsOpen, setDocsOpen] = useState(false);
  /* BRIEF 105 §3b — THE THIRD DRAWER. Local, like `docsOpen`: a per-row
     disclosure no host asks about.
     §3b-ii's gate is `canEdit` — the same gate the title, the date, the type,
     the level and the assignees on this card already use, all of which write
     this row through the same route. */
  const [notesOpen, setNotesOpen] = useState(false);
  /* 110 §1h — the host's open-all/close-all broadcast; see CaseTaskRow. */
  useEffect(() => {
    if (!drawersCast || !drawersCast.seq) return;
    /* 111 §2 — open only what HAS something (the icons' own facts,
       :506/:515); close stays total. */
    setDocsOpen(!!drawersCast.open && liveDocs.length > 0);
    setNotesOpen(!!drawersCast.open && hasNoteText(ev.notes)); // 118 §9
  }, [drawersCast]);

  const evDate = ev.date ? ev.date.slice(0, 10) : "";
  // BRIEF 102 §2f — the diff and the words come from src/lib/dayCountdown.ts
  // now. Same arithmetic, same three branches, same sign convention; this file
  // and AgendaView had written them separately and identically.
  const diff = dayDiff(evDate, todayStr);
  const isPast = diff !== null && diff < 0;
  const isToday = diff === 0;
  const label = dayCountdownLabel(diff, t);
  const color = evDayClr(diff, ev.type);
  /* BRIEF 136 §3 — the row's own status, over all four codes: the column when
     it carries one, `confirmed` when it does not, and `archived` from the
     Phase-28 boolean (`eventStatusOf`). The taxonomy is read here rather than
     threaded in, for the reason `SubtaskChecklist.tsx:144` gives: this row
     mounts under several hosts and one more prop through all of them is how two
     surfaces come to answer one question differently. */
  const { entries: evStatusEntries } = useTaxonomy("event_status");
  const evStatus = eventStatusOf(ev);

  const assignees = ev.assignedCollaborators || [];
  // Owner/assignee of this event (mirrors the task gate's `isOwn`): assigned to
  // me, or unassigned. canEditOthersTasks preserves the original "edit others"
  // path for the collective view.
  const assigneeOk =
    !assignees.length || assignees.includes(currentUser) || canEditOthersTasks;
  // Superset gate (matches CaseTaskRow's `isOwn || canEditOthersTasks` shape):
  // the agenda-edit bit grants edit/delete on ANY event; owning/being assigned
  // grants it on your own. A user who is neither is blocked.
  const canEdit = !locked && (canEditAgenda || assigneeOk);
  /* 109 §1d — the rights half without the lock: a note is jotted at any time;
     the padlock gates the card's structure, never the note. */
  const notesOk = !archived && (canEditAgenda || assigneeOk); // 195 §A — nothing is jotted on an archived card

  const patchEvent = (patch: any) =>
    s(
      SK.ag,
      ag.map((a: any) => (a.id === ev.id ? { ...a, ...patch } : a)),
      sAg,
    );

  /* BRIEF 117 §1 — UNSET is null and renders nothing; SET always renders
     something. The second half is what makes the first half safe: with the
     `canEdit` em-dash gone (below), a code the ladder no longer resolves — a
     level deleted out from under the event — would otherwise render NOTHING and
     look unset. `provisionalLevel` shows the stored code, muted: exactly what
     the task row has done since 104 §1a (CaseTaskRow.tsx:209). */
  const pri = ev.priority
    ? priMap[ev.priority] || provisionalLevel(ev.priority)
    : null;

  // 154B §2 — the ref callback that focused the mounted <select> and called
  // showPicker() on it is `autoOpen`; one click on the cell still opens the
  // list directly. See CaseTaskRow, which lost the same twelve lines.

  /* 216 addendum 7 §11 (2) [RULED 84] — THE THREE DRAWERS, ONCE, PLACED BY THE HOST: as siblings under the card
     (a matter's, a collective case's, the Archive's — 91 §3b's and 98 §3's join rules read the adjacency),
     or INSIDE the card's content on the Dashboard (`dashboardCard`) — Laurent, 16/10: an event's « notes,
     documents and subtasks » open inside the card, as a task's do. The three mounts are the same; only
     where they land and the frame they wear (`evDrawerIn`: the wash, no border — the card is the frame)
     differ. */
  const drawers = (
    <>
      {/* BRIEF 98 §2 — SUBTASKS FIRST, THEN DOCUMENTS, which is the task row's
          order (CaseTaskRow.tsx: SubtaskChecklist then TaskDocsPane). 95 §3b
          said events copy what tasks end up with and the order is part of that;
          the mount went in above the subtasks and the comment beside it said
          "framed exactly as the panel above it" when there was no panel above
          it. The comment described the task row and the JSX did the reverse. */}
      {evExpanded.has(ev.id) && (
        <EventSubtasks
          event={ev}
          inside={dashboardCard} /* 216 addendum 7 §11 (2) — inside the Dashboard's card: no frame of its own */
          locked={locked}
          /* 115 §2 — the Dashboard's view-only regime (110 §1b), through the
             prop the panel has carried since 110. */
          readOnly={readOnly}
          /* brief 90 §3a — see SubtaskCounter: without these the assignee
             cannot be set here and an existing one renders as nothing.
             brief 91 §3a — and without these three, no timer and no time log,
             which was the same omission a second time. */
          co={co}
          currentUser={currentUser}
          activeTimers={activeTimers}
          startTimer={startTimer}
          openModal={openModal}
          onItemsChange={(arr: any) =>
            sAg(ag.map((a: any) => (a.id === ev.id ? { ...a, subtasks: arr } : a)))
          }
        />
      )}
      {/* BRIEF 98 §3 — `subtaskDrawer evSubDrawer`, BOTH, exactly as
          `EventSubtasks` mounts it (SubtaskCounter.tsx:258).
          95 §3b took only the second half, and the halves are not
          interchangeable: `.evSubDrawer` supplies the frame (three sides, the
          two bottom corners, the 40px indent) and `.subtaskDrawer` supplies the
          COLOUR — brief 90 §3b's `color-mix(--cb 14%, transparent)` wash. With
          the frame and no wash the documents pane rendered as a bordered card
          in the surface colour beside a grey subtasks panel, which is two of
          the three cards §3 is about. */}
      {docsOpen && (
        <div className={dashboardCard ? "subtaskDrawer evDrawerIn" : "subtaskDrawer evSubDrawer"}>
          <TaskDocsPane
            eventId={ev.id}
            initialDocs={Array.isArray(ev.docs) ? ev.docs : undefined}
            /* 115 §2 — quiet under the Dashboard regime (110 §1b). */
            canWrite={readOnly ? false : docsCanTouch}
            locked={locked}
            onDocsChange={(arr) =>
              /* 151 §2 step 4 — FUNCTIONAL, where it read the captured `ag`:
                 the stage-link pen below (patchEvent → the s() engine) writes
                 this same array in the same batch, and a lift computed from
                 the render-scope copy would erase whichever write it followed.
                 Functional, it composes with anything already queued. */
              sAg((prev: EvRow[]) =>
                (Array.isArray(prev) ? prev : []).map((a) =>
                  a.id === ev.id ? { ...a, docs: arr } : a,
                ),
              )
            }
            /* 110 §1a — the stage drawer's binding (98 §4's oneDriveMatter,
               reduced to the {caseId|ccId} the picker needs); the pane owns
               the button and the write. Same gate as the retired slot. */
            oneDrive={
              !reduced && docsCanTouch && oneDriveMatter
                ? oneDriveMatter.kind === "cc"
                  ? { ccId: oneDriveMatter.matter.id }
                  : { caseId: oneDriveMatter.matter.id }
                : undefined
            }
            /* 151C §6 — no pen here any more: the pane owns the purple-link
               reconcile (lib/stageItemLink); the stageId write is retired
               from this flow — the event's belongs-to stage stays its own
               StageLinkField's. Dashboard/compact mounts pass no
               oneDriveMatter and stay link-less, exactly as they stay
               picker-less. */
            stageLink={
              oneDriveMatter && Array.isArray(oneDriveMatter.matter?.stages)
                ? {
                    stages: oneDriveMatter.matter.stages,
                    matterKind: oneDriveMatter.kind === "cc" ? ("cc" as const) : ("ca" as const),
                    matterId: oneDriveMatter.matter.id,
                    matterRow: oneDriveMatter.matter,
                  }
                : undefined
            }
          />
        </div>
      )}
      {/* ── BRIEF 105 §3b-i — THE THIRD DRAWER, AND THE JOIN RULES ───────────
          "98 §3 exists BECAUSE 95 added a second drawer and the one-drawer rule
           broke silently. A third is the same event a third time."

          IT IS NOT, AND THE DIFFERENCE IS WORTH WRITING DOWN. 91 §3b's rule was
          `.event-item:has(+ .evSubDrawer)` — a card with A drawer under it —
          which is already true for any number. What 95 broke was that each
          DRAWER still rounded its own bottom, and 98 §3 fixed it with
          `.evSubDrawer:has(+ .evSubDrawer)`: a drawer with ANOTHER AFTER IT is
          squared. That predicate is N-ary, not 2-ary — "another after it" does
          not count how many. So the pair of rules already closes the rectangle
          for three, and for four, with nothing added.

          WHAT IT DEPENDS ON IS ADJACENCY, and that is the thing this mount had
          to get right: `+` is the adjacent-sibling combinator, so a drawer that
          is not an immediate sibling of the one before it breaks the chain.
          React renders nothing for a false branch, so a closed drawer leaves no
          element between two open ones and the chain holds in every
          combination. All eight were checked in a browser and are listed in the
          PR body.

          `subtaskDrawer evSubDrawer`, BOTH, for 98 §3's reason: the second
          supplies the frame and the first supplies the colour. */}
      {notesOpen && (
        <div className={dashboardCard ? "subtaskDrawer evDrawerIn" : "subtaskDrawer evSubDrawer"}>
          <NotesDrawer
            value={ev.notes}
            /* 109 §1d — rights without the lock (see notesOk above).
               115 §2 — which is why the Dashboard regime is its own prop:
               notes quiet there (110 §1b), and no lock can say so. */
            canWrite={readOnly ? false : notesOk}
            /* §3b-ii — `patchEvent` is `s(SK.ag, …, sAg)`, the write path every
               inline edit on this card already uses, and the same engine and
               key `saveAgendaEvent` (App.tsx:9942) writes through.
               No second path. */
            onSave={(next) => patchEvent({ notes: next })}
          />
        </div>
      )}
    </>
  );
  return (
    <Fragment>
      <div
        id={"ev-" + ev.id}
        data-lnk={"event-" + ev.id}
        className={dashboardCard ? "event-item dashCard" : "event-item"}
        /* BRIEF 91 §3b — the inline `borderRadius: RAD.r8` is gone. It was a
           byte-for-byte repeat of `.event-item`'s own `border-radius:
           var(--rad-8)` (App.css:693, and RAD.r8 === 8 === --rad-8), and being
           inline it outranked the class — so §3b's rule could square the bottom
           corners of the two hosts that never set it and not of this one. The
           two other event cards (AgendaView:490, Dashboard:1132) already take
           the radius from the class alone. */
        /* 115 §2 — the Dashboard's card-click semantics. Only the card
           carries it: the drawers are SIBLINGS, so a click inside an open
           drawer navigates nowhere by construction. */
        onClick={onRowNav && !archived ? () => onRowNav(ev) : undefined}
        style={{
          /* 195 §A.4 — archived: the ONE dimming, in place of the past
             event's own 0.4 (an archived event is usually past; two dims
             compounding would be a third value nobody chose). */
          ...(archived ? { opacity: DIMMED_ROW_OPACITY } : {}),
          ...(!archived && isPast ? { opacity: 0.4 } : {}),
          ...(onRowNav && !archived ? { cursor: "pointer" } : {}),
        }}
      >
        {/* Date block — omitted in the reduced (compact-column) variant:
            the column is a today-only horizon. 191 (2) — never switchable:
            no key. */}
        {!reduced && (
        /* BRIEF 99 §1b — the three lines centre as ONE GROUP on the row's axis.
           `.event-item` is `align-items: flex-start`, so this block sat at the
           top of a card whose height is set by the content beside it. The class
           takes `align-self: stretch` and centres its own column, which changes
           where the group sits and NOT how tall the row is — §4's stop asks
           about the second and the row's height is still the content's. */
        /* BRIEF 101 §1 — the shape is `DateBlock` now; the EMPTY STATE is not,
           and that is §1a's ruling rather than an omission. This host's empty
           block is the wrapper, a muted day carrying the em-dash affordance and
           an EMPTY MONTH LINE — which is not decoration: it occupies its 10px
           and is why the card does not jump when a date is set. `CaseTaskRow`'s
           empty state is a bare dash with no wrapper at all. One `empty` prop
           could not have been both, so neither is in the component. */
        ev.date ? (
          <DateBlock
            date={ev.date}
            onDayClick={canEdit ? () => { setEdit("date"); } : undefined}
            dayTitle={canEdit ? "Click to change date" : undefined}
          />
        ) : (
          <div className="date-block">
            <div
              className="date-day"
              onClick={canEdit ? () => { setEdit("date"); } : undefined}
              style={{
                ...(canEdit ? { cursor: "pointer" } : {}),
                color: "var(--tm)",
              }}
              title={canEdit ? "Click to change date" : undefined}
            >
              {canEdit ? "—" : ""}
            </div>
            <div className="date-month" />
          </div>
        )
        )}
        <div className="event-content">
          {/* BRIEF 137 §1 — THE WRAP IS NOW CONDITIONAL, AND BOTH RULINGS
              SURVIVE BY CONSTRUCTION.
              94 §3b enumerated two failures — overflow the card, or take a
              second line — and picked the wrap as the better one. It named
              the thing the wrap genuinely protects: the assignee EDITOR, which
              opens inline at `minWidth: 200` (:404). That is still true and
              the wrap is still there FOR IT.
              What §3b did not have is the third answer, which by then already
              existed on the other event surface: `AgendaView.tsx:566-583`
              (102 §1a/§1c) is `flexWrap: "nowrap"` with the title span as the
              row's shrink sink, and it holds the same case on ONE line,
              ellipsised. So the two surfaces disagreed and the LIST one was
              right.
              `edit` is this file's inline-editor state — null | 'title' |
              'date' | 'priority' | 'type' | 'assignee' (:152), every one of
              them a control that opens INSIDE this row. So `edit ? wrap :
              nowrap` is the honest spelling of "an editor is open": the
              resting row is one line with an ellipsised title (102 §1a), the
              editing row still gets its second line (94 §3b).
              The title span does NOT take `flexGrow`. 95 §3a(b) rules that the
              documents and notes toggles sit hard against the text they belong
              to and are "never pushed to the card's right edge", and a growing
              title is exactly that push. `flex: "0 1 auto"` is shrink-only. */}
          <div
            className="event-title"
            style={{
              display: "flex",
              alignItems: "center",
              gap: TITLE_GAP,
              flexWrap: edit ? "wrap" : "nowrap",
            }}
          >
            {reduced && lead !== undefined && lead}
            {fur("furniture.event.type") && (edit === "type" ? (
              <MenuSelect
                className="field-input"
                style={{ width: "auto", maxWidth: 200, margin: 0, fontSize: FS.s11, padding: "1px 4px" }}
                autoOpen
                value={ev.type || ""}
                onChange={(v) => {
                  patchEvent({ type: v || null });
                  setEdit(null);
                }}
                onBlur={() => setEdit(null)}
                options={[
                  ["", t("common.select_dashed", { fallback: "— Select —" })] as const,
                  /* 110 §3a's divider is 108 §6b's row now */
                  ...evtTypes.map(
                    (ty: any) =>
                      [
                        String(ty.code),
                        isDividerCode(ty.code)
                          ? String(ty.desc || "")
                          : `${ty.code} — ${ty.desc}`,
                      ] as const,
                  ),
                ]}
              />
            ) : (
              /* BRIEF 118 §8 — THE CHIP TAKES THE TASK COLUMN'S BOX. The click
                 target is the BOX, not the chip: a fixed column whose
                 affordance is only as wide as the code would be a different
                 target on every row. */
              <span
                onClick={canEdit ? () => setEdit("type") : undefined}
                style={{
                  cursor: canEdit ? "pointer" : undefined,
                  display: "inline-flex",
                }}
                title={canEdit ? "Click to change type" : undefined}
              >
                <TypeTagBox reduced={reduced} mirror={_typeMirror}>
                  {etTag(ev.type, reduced)}
                </TypeTagBox>
              </span>
            ))}
            {/* BRIEF 94 §3a — THE ASSIGNEES MOVE ONTO THE TITLE LINE,
                between the type tag and the counter: INTRO · TAA · 0/2 ▸ ·
                title. They lived in `.event-meta`, which is a SECOND div by
                construction — so the badge was never wrapping onto a second
                line, it was rendered onto one, and the card spent a row on it.

                §3c — THE CLICK TARGET MOVES WITH IT, WHOLE. Both branches
                travel: the resting badges and the `edit === "assignee"`
                MultiSelect, so the editor opens where the badges are rather
                than in the row they just left. The `onClick`, the `title` and
                the em-dash affordance ride on THIS span and nothing was
                widened — the type tag and the counter are siblings before and
                after, each with its own handler, so neither can open the
                assignee editor. */}
            {fur("furniture.event.assignees") && (reduced ? null : edit === "assignee" && MultiSelect ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 200,
                }}
              >
                <MultiSelect
                  l=""
                  opts={choosableCo(co, ev.assignedCollaborators)}
                  sel={ev.assignedCollaborators || []}
                  onChange={(v: any) => patchEvent({ assignedCollaborators: v })}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: "2px 4px", fontSize: FS.s9, flexShrink: 0 }}
                  title={t("common.done", { fallback: "Done" })}
                  onClick={() => setEdit(null)}
                >
                  <Check size={I.s} />
                </button>
              </span>
            ) : (
              <span
                onClick={canEdit ? () => setEdit("assignee") : undefined}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: canEdit ? "pointer" : undefined,
                }}
                title={canEdit ? "Click to edit assignees" : undefined}
              >
                {/* BRIEF 95 §2c — the event goes in as the second argument.
                    This row holds neither `cases` nor `collectiveCases`, which
                    is exactly why the resolution lives in `coAvs` (App.tsx:7058)
                    rather than at the call sites: nothing has to be threaded
                    down to reach it, and §5's 2c stop never fires. */}
                {/* 116 §2 — the "no assignees" em-dash that stood here is
                    `coAvs`'s now: the badge area reserves its own width, so a
                    dash BESIDE it would have sat next to an empty 38px box.
                    One owner for the whole area, in the one renderer every
                    event surface calls. */}
                {coAvs ? coAvs(ev.assignedCollaborators, ev) : null}
              </span>
            ))}
            {/* 116 §3 [RULED] — THE EVENT LEARNS TO TELL TIME. The same
                `RowTimerBlock` the task row renders, in the same NEIGHBOURHOOD:
                the task row's cell order is [assignee][timer][counter] and this
                card's meta line now reads the same way, so the two rows Laurent
                glances side by side put the control in the same place rather
                than in two invented ones.
                The block is withheld on an ARCHIVED event — the task row's own
                predicate is "no work left to time here" (completed or
                archived), and an event has no completion state, so archived is
                the whole of it.
                No schema: `TimeEntry` has no task column either
                (schema.prisma:825-851), so a task timer already bills SOFTLY
                through this same modal and an event timer needs nothing new. */}
            {!ev.archived && !archived && lead === undefined && typeof startTimer === "function" && fur("furniture.event.timer") && (
              /* THE WRAPPER IS LOAD-BEARING, and the ruler said so before this
                 comment did: the two buttons are direct children of a flex row
                 carrying `gap: 7`, so the pair measured 46px with 8px between
                 the glyphs where the task row's reads 39 with 1. That is 115
                 §4's defect exactly — a host's own geometry leaking into a
                 shared control — so the block takes its own inline-flex box and
                 the card's gap applies to the BOX. Re-measured after: 39 · 1 ·
                 8 · 20 · 18, identical to the task row on every number. */
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
              <RowTimerBlock
                timer={(activeTimers || []).find(
                  (x: any) => x && x.eventId === ev.id && !x.subtaskId,
                )}
                onStart={() => startTimer(null, null, { eventId: ev.id })}
                logTitle={t("agenda.log_time_tip", {
                  fallback: "Add a time entry on this event",
                })}
                onLog={() =>
                  openModal("te", {
                    caId: ev.caseId ?? ev.caId ?? "",
                    ccId: ev.ccId ?? "",
                    coId: currentUser || "",
                    date: new Date().toISOString().slice(0, 10),
                    type: ev.type || "",
                    desc: ev.title || "",
                    billed: false,
                    min: 0,
                    /* An event is not a task: `_fromTask` would be a "there is
                       one, I cannot name it" claim the server acts on
                       (subtaskTiming.ts:108 made the same call for event
                       subtasks). The entry binds through its matter. */
                  })
                }
              />
              </span>
            )}
            {fur("furniture.event.subtasks") && (
            <SubtaskCounter
              subtasks={ev.subtasks}
              todayStr={todayStr}
              open={!archived && evExpanded.has(ev.id)}
              onToggle={archived ? undefined : () => toggleEvExpanded(ev.id)} /* 195 §A — inert */
              /* 115 §2 — withheld under the regime: the empty-state "+" says
                 "click to add the first" and the drawer it opens refuses
                 adds. The Dashboard's events pane never offered one (it
                 passed no canAdd), and an affordance that lies is the 108
                 §2a class.
                 BRIEF 117 §7 — and withheld under the LOCK, for the same
                 sentence: 116 §1 took the add out of the drawer this opens. */
              canAdd={subtaskCanAdd}
            />
            )}
            {/* Level (priority) — inline editable when unlocked.
                Omitted when reduced. */}
            {fur("furniture.event.level") && (reduced ? null : edit === "priority" ? (
              <MenuSelect
                className="field-input"
                style={{ width: "auto", margin: 0, fontSize: FS.s11, padding: "1px 4px" }}
                autoFocus
                value={ev.priority || ""}
                onChange={(v) => {
                  patchEvent({ priority: v || null });
                  setEdit(null);
                }}
                onBlur={() => setEdit(null)}
                options={[
                  ["", t("common.unset_dashed", { fallback: "— Unset —" })] as const,
                  ...itemLevelList.map(
                    (o: any) => [String(o.v), String(o.l ?? "")] as const,
                  ),
                ]}
              />
            ) : pri ? (
              <span
                onClick={canEdit ? () => setEdit("priority") : undefined}
                style={{
                  fontSize: FS.s11,
                  fontWeight: 700,
                  color: pri.c,
                  flexShrink: 0,
                  cursor: canEdit ? "pointer" : undefined,
                }}
                title={canEdit ? "Click to change level" : undefined}
              >
                {pri.l}
              </span>
            ) : null)}
            {/* BRIEF 117 §1 — the `canEdit` arm that stood here drew an em-dash
                so an unset level had something to click. It is gone: an unset
                level renders nothing, on every host. Measured on 2026/001 before
                the change — LOCKED the card already drew nothing (`canEdit` is
                `!agLocked && …`, :200), UNLOCKED it drew a 10px dash on three of
                five cards, which is the whole of what "events already do this"
                was resting on. The level is set from the ag modal, as the task
                row's is from the task modal. */}
            {/* Title / date — inline editable when unlocked. 191 (2) — never
                switchable: no key. */}
            {edit === "title" ? (
              <input
                className="field-input"
                size={Math.min(80, Math.max(24, (ev.title || "").length + 4))}
                style={{ width: "auto", margin: 0, fontSize: FS.s13, padding: "1px 4px" }}
                autoFocus
                defaultValue={ev.title || ""}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== ev.title) patchEvent({ title: v });
                  setEdit(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = (e.target as any).value.trim();
                    if (v && v !== ev.title) patchEvent({ title: v });
                    setEdit(null);
                  } else if (e.key === "Escape") {
                    setEdit(null);
                  }
                }}
              />
            ) : (
              <span
                onClick={
                  reduced && onNavEvent
                    ? () => onNavEvent(ev)
                    : canEdit
                      ? () => setEdit("title")
                      : undefined
                }
                style={{
                  cursor:
                    reduced && onNavEvent
                      ? "pointer"
                      : canEdit
                        ? "text"
                        : undefined,
                  color: ev.title ? undefined : "var(--tm)",
                  /* BRIEF 137 §1(2) — AgendaView.tsx:695-698's four
                     properties, verbatim: this span is the row's shrink sink.
                     Every sibling on the line keeps `flexShrink: 0`, so a
                     short title renders exactly as it did and only a long one
                     ellipsises at the rendered edge. `flex: "0 1 auto"` is
                     stated rather than left to the initial value because the
                     ONE thing this span must never gain is grow (95 §3a(b)). */
                  flex: "0 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={
                  reduced && onNavEvent
                    ? t("col.open_in_case_tip", { fallback: "Open in case" })
                    : canEdit
                      ? "Click to rename"
                      : undefined
                }
              >
                {ev.title || (canEdit ? "—" : "")}
              </span>
            )}
            {/* BRIEF 95 §3b — the documents toggle, in §3a(b)'s place: hard
                against the text it belongs to, `flexShrink: 0`, never pushed to
                the card's right edge. Its sentence used to end "byte-for-byte
                the task row's control … so the two rows Laurent glances side by
                side carry one affordance and not two spellings of one".
                BRIEF 107 §2b MAKES THAT TRUE BY CONSTRUCTION rather than by
                copying, so the claim is withdrawn along with the copy it
                described: there are no longer two spellings to keep in step,
                there is one component. Same reason 99 withdrew 91 §3b — a
                comment that argues from a mechanism expires when the mechanism
                does. */}
            {/* BRIEF 117 §7 — `docsCanAdd`. See CaseTaskRow.tsx's twin: the
                `|| canTouch` arm of `docsToggleVisible` is what makes an
                EMPTY drawer reachable, and an empty drawer that refuses adds
                is nothing to reach. Rows that hold documents are unaffected. */}
            {fur("furniture.event.toggles") && docsToggleVisible(liveDocs, docsCanAdd) && (
              <DocsToggle
                open={docsOpen}
                hasDocs={liveDocs.length > 0}
                onToggle={archived ? undefined : () => setDocsOpen((v) => !v)}
              />
            )}
            {/* §3b — "a NotebookPen icon BESIDE the documents one".
                109 §1d — ALWAYS rendered on the surfaces that carry the
                documents icon: muted when empty, filled when a note exists. */}
            {fur("furniture.event.toggles") && (
            <NotesToggle
              open={notesOpen}
              hasNotes={hasNoteText(ev.notes)} /* 118 §9 */
              onToggle={archived ? undefined : () => setNotesOpen((v) => !v)}
            />
            )}
            {/* 111 §5c — the Link icon, when the event HAS links. */}
            {fur("furniture.event.toggles") && <LinkIcon matterRow={matterRow} item={{ k: "event", id: ev.id }} />}
            {/* 109 §7f — the link pill, event side: where this event carries a
                stageId AND the host threads the stages feed (the CV/CCV agenda
                sections do; the compact column does not, so it renders none),
                a pill naming the stage TYPE; click = jump + flash to the
                timeline row via scrollToStage. */}
            {fur("furniture.event.toggles") &&
              ev.stageId != null &&
              Array.isArray(stages) &&
              (() => {
                const _stg = (stages as { id?: number; type?: string | null }[]).find(
                  (s2) => Number(s2.id) === Number(ev.stageId),
                );
                if (!_stg) return null;
                /* 110 §6c — the pill says the ACTION ("Show Stage"); the
                   stage's NAME moves to the tooltip, which falls back to the
                   old jump hint when the type resolves to nothing. */
                const _stgName =
                  resolveTaxonomyLabel(_stg.type, caseStageEntries) ||
                  String(_stg.type || "");
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollToStage(ev.stageId);
                    }}
                    title={
                      _stgName ||
                      t("stage.pill.jump_tip", {
                        fallback: "Show the linked stage",
                      })
                    }
                    style={{
                      fontSize: FS.s9_5,
                      color: "var(--tm)",
                      background: "rgba(255,255,255,.07)",
                      padding: "1px 5px",
                      borderRadius: RAD.r8,
                      flexShrink: 0,
                      border: "1px solid var(--br)",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("stage.show_stage", { fallback: "Show Stage" })}
                  </button>
                );
              })()}
            {edit === "date" && (
              /* BRIEF 95 §3a(a) via §3b — the event row takes the task row's
                 date control, so the two rows Laurent puts side by side do not
                 disagree about what a date looks like. ISO on the wire, as
                 before. */
              <DateField
                wrapStyle={{ margin: 0 }}
                style={{ width: "auto", margin: 0, fontSize: FS.s12, padding: "1px 4px" }}
                autoFocus
                value={evDate}
                onChange={(v) => patchEvent({ date: v })}
                onBlur={() => setEdit(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") setEdit(null);
                }}
              />
            )}
            {/* BRIEF 102 §4a — THE DAY LABEL GOES TO THE CARD'S RIGHT EDGE.
                MEASURED before: its right edge sat 401px short of the card's,
                because it rendered immediately after the documents toggle and
                nothing pushed it. `marginLeft: auto` is the whole change, and
                it lands the label at §3a's number for free — the card's own
                `padding-right: 9px` plus its 1px border is the same 10px §3a
                took from this card's LEFT.
                THE ROW DOES NOT JUMP WHEN THE LABEL IS ABSENT, which §4a asks
                for explicitly and an auto margin gives by construction: the
                margin belongs to the label, so with `label === null` there is
                no margin and every other child sits exactly where it did. The
                title keeps the slack — it is the row's only shrink sink. */}
            {/* BRIEF 136 §3(3) — the counter, or the glyph that replaces it.
                The markup moved to `EventDayMark` because `AgendaView.tsx:742`
                draws the same thing and must not draw it differently; the
                COLOUR stays here, where it is computed. */}
            {/* 216 addendum 7 §11 (2) — on the Dashboard's card the mark sits in its own column (below) */}
            {!reduced && !dashboardCard && fur("furniture.event.daymark") && (
              <EventDayMark
                label={label}
                color={color}
                isPast={isPast}
                status={evStatus}
                statusEntries={evStatusEntries}
              />
            )}
          </div>
          {/* 216 addendum 4 §8 (3) [RULED 79] — MORE ROOM BETWEEN THE TWO ROWS.
              Laurent, 14/10: « the place name almost bites the assignee icon
              when circled ». The ringed badge is 22 px plus a 1.5 px ring on a
              title line whose text box is shorter, so the ring reached the meta
              line's top. The reduced (compact column) variant is out of §8. */}
          <div className="event-meta" style={reduced ? undefined : { marginTop: EVENT_ROWS_GAP }}>
            {/* 115 §2 — the matter chip (Dashboard host; see the prop note).
                stopPropagation because the card itself may navigate to the
                same place — one jump, not two. The 20-char cut is BRIEF 102
                §1a's, moved with the pill it governs: `.lib-pill` is
                inline-flex with no max-width, so it cannot ellipsise itself
                (App.css:666).
                BRIEF 118 §7 — AND IT LEADS THE LINE NOW, AT THE TIMER'S x.
                MEASURED before, on watt's Dashboard: the chip sat at 290 on
                two cards and 326 on a third — it followed `ev.time` and
                `ev.location`, so its x was the row's content and there was no
                column to read down.
                THE INSET IS NOT A NUMBER, and 114 §1's ruling is why: the
                title line's leading run is [type][gap][badge area], and the
                type chip is CONTENT-SIZED (`etTag`, in App.tsx, declares no
                width at all), so any pixel indent would be right for one type
                and wrong for the next. The card already knows the number,
                because it renders the same content: an `aria-hidden`,
                `visibility: hidden` MIRROR of that run, at the title line's
                own `TITLE_GAP`, puts the chip's left edge exactly on the
                timer block's, at any type width and any zoom — before §8
                fixes that width and after it. `visibility: hidden` is not
                `display: none`: the box is laid out, which is the whole
                point, and it is not a hit target.
                It leads the line, so `ev.time` and `ev.location` follow the
                chip on the Dashboard's cards. They led it before and their
                own x moved with each other; the chip is what Laurent named,
                and reserving the run for every card would move the CV/CCV
                cards, which pass no chip and are out of §7's sentence. */}
            {!reduced && chip && fur("furniture.event.matter") && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: TITLE_GAP,
                  flexShrink: 0,
                  /* 216 addendum 4 §8 (4) [RULED 79] — the Dashboard's chip at the
                     right end of the second row, after everything else on it
                     (`order`), its left margin taking the slack; the mirror that
                     put it at the timer's x (118 §7) is not drawn for it. */
                  ...(chip.atRight ? { marginLeft: "auto", order: 99, minWidth: 0 } : {}),
                }}
              >
                {!chip.atRight && (
                <span
                  aria-hidden="true"
                  style={{
                    visibility: "hidden",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: TITLE_GAP,
                    flexShrink: 0,
                  }}
                >
                  {/* 118 §8 — the SAME box the title line now renders, so the
                      mirror cannot drift from what it mirrors. It resolved to a
                      constant then; 139 §1 is it "ever not", and this line needed
                      no change for it — the box is the same box, and it now
                      carries the same `mirror` too, so the chip still lands on
                      the timer's x at whatever width the taxonomy sets. */}
                  <TypeTagBox reduced={reduced} mirror={_typeMirror}>
                    {etTag(ev.type, reduced)}
                  </TypeTagBox>
                  <span style={{ display: "inline-block", width: BADGE_AREA_W }} />
                </span>
                )}
                {/* 216 addendum 6 §10 (2) — the chip's one definition (MatterChip.tsx), moved out of this file
                    unchanged so a task's card wears the same pill */}
                <MatterChip chip={chip} />
              </span>
            )}
            {reduced && caseLine ? (
              <span
                title={caseLine}
                style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {caseLine}
              </span>
            ) : null}
            {ev.time && <span>{ev.time}</span>}
            {/* BRIEF 124 §4 — THE ROOM IS THE LOCATION ON SCREEN.
                Laurent: "the Event should show not the address of the firm, but
                the place reserved (both though in invitation email)."

                `roomName` is derived by the server on every read from the
                event's own bound `PlaceReservation` (routes/agenda.js `shape`)
                — never stored, so releasing or moving a reservation is
                reflected without a sweep. It WINS over the free text, which is
                usually `mtgDefLoc`, i.e. the firm's address: a row that says
                "Board room" is useful and a row that says the street the reader
                is standing in is not.

                `ev.location` is NOT destroyed and is NOT hidden from the guest
                — the invitation carries both (`inviteLocationFor`). This is a
                display preference on one surface, not a data change. */}
            {/* 216 addendum 4 §8 (3) [RULED 79] — THE PLACE WITHOUT ITS ADDRESS:
                the location up to its first comma (lib/placeName.ts — the
                automatic location is written « name, address »), the whole on
                hover; a booked room as before. The icon still reads the whole
                (it classifies the place). */}
            {!reduced && place.shown && fur("furniture.event.room") && (
              <span
                title={place.whole ?? undefined}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  color: "var(--ts)",
                }}
              >
                {locIcon ? locIcon(ev.roomName || ev.location) : null}
                {place.shown}
              </span>
            )}
            {/* BRIEF 105 §3b — THE NOTE LEAVES THE META LINE. On this card it
                was never truncated — `.event-meta` is `flex-wrap: wrap`
                (App.css:837) and clips nothing — so the fault here is the
                other half of Laurent's sentence: an unlabelled span among the
                time, the location, the case pill and the document link, and
                the only way to CHANGE it was the modal. It is a drawer now,
                and it is editable there. */}
            {/* Phase 31 (C4) — Join affordance (compact in the reduced
                column variant). Covers CV / CCV / CompactColumn rows. */}
            {/* 195 §A — no join affordance on an archived card: a link is a
                control, and the archived row's only two are the host's. */}
            {!archived && fur("furniture.event.room") && <JoinBtn url={ev.joinUrl} stub={!!ev.joinUrlStub} compact={reduced} />}
            {!reduced && ev.docLink && fur("furniture.event.room") && (
              <span>
                <a
                  href={ev.docLink}
                  target="_blank"
                  rel="noopener"
                  /* 195 §A — the link's TEXT stays (it is content); the anchor
                     goes inert the way the invoiced row's buttons do
                     (TimeEntryTable: `pointerEvents: "none"`), no underline. */
                  style={{
                    color: "var(--ac)",
                    textDecoration: archived ? "none" : "underline",
                    fontSize: FS.s9_5,
                    ...(archived ? { pointerEvents: "none" as const } : {}),
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <File
                    size={9}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: 2,
                    }}
                  />
                  {/* BRIEF 102 §1a — KEPT, second branch: a URL preview, not a
                      layout cut. See Dashboard.tsx:1258. */}
                  {ev.docLink.length > 50
                    ? ev.docLink.slice(0, 50) + "…"
                    : ev.docLink}
                </a>
              </span>
            )}
          </div>
          {/* 216 addendum 7 §11 (2) [RULED 84] — THE DASHBOARD'S CARD: the content is a two-column grid (App.css
              `.dashCard`): the title row and the meta row in the first column, the counter in the second spanning
              both rows and centred on them — the case pill, last on the meta row, sits beside it — and the drawers
              under the rows, inside the card, which grows to hold them. */}
          {dashboardCard && !reduced && fur("furniture.event.daymark") && (
            <span className="evDayMark" data-ev-daymark="">
              <EventDayMark label={label} color={color} isPast={isPast} status={evStatus} statusEntries={evStatusEntries} />
            </span>
          )}
          {dashboardCard && <div className="evDrawersIn">{drawers}</div>}
        </div>
        {/* 191 (2) — the actions are never switchable: no key. */}
        {canEdit && (
          <button
            className="btn btn-ghost"
            style={{ padding: "1px 4px", fontSize: FS.s9 }}
            onClick={() => openModal("ag", { ...ev, _caseFixed: true })}
          >
            <Pencil size={I.s} />
          </button>
        )}
        {/* BRIEF 136 §3(4) [RULED] — "For events the archive icon appears
            ALWAYS", and the ruling states it as the deliberate OPPOSITE of §2's
            rule for tasks, where the icon waits until the status carries a
            reason.

            MEASURED, IT ALREADY WAS: this button has only ever been gated on
            `canEdit` (= `!agLocked && (canEditAgenda || assigneeOk)`) and never
            on a status, because until this brief an event had no status to be
            gated on. So §3(4) adds no condition — it RECORDS that the absence
            of one is now a decision rather than an accident, and stops the next
            reader from "completing" §2's pattern here.

            WHY THE TWO DIFFER, in the ruling's own terms: on a task the status
            that precedes an archive IS the reason ("so one cannot archive
            without mentioning why"). An event's statuses do not carry that
            meaning — an event is confirmed, unconfirmed or cancelled, and none
            of the three is a prerequisite for filing it away. A confirmed
            hearing that has simply happened is the ordinary thing to archive.

            The lock still gates it, as it gates everything structural on this
            card (109 §1d). "Always" is about the STATUS, not about the padlock. */}
        {canEdit && (
          <button
            className="btn"
            style={{
              padding: "1px 4px",
              fontSize: FS.s9,
              background: "rgba(245,158,11,.12)",
              color: "var(--am)",
              border: "1px solid rgba(245,158,11,.30)",
              borderRadius: RAD.r4,
              cursor: "pointer",
            }}
            onClick={() => onDeleteEvent(ev)}
            title={t("common.archive", { fallback: "Archive" })}
          >
            <Archive size={I.s} />
          </button>
        )}
        {/* 195 §A — the archived row's two actions, the host's, where the
            pencil and the archive button stand on a live card. */}
        {archived ? archivedActions : null}
      </div>
      {/* 216 addendum 7 §11 (2) [RULED 84] — every host but the Dashboard keeps its drawers ATTACHED under the
          card as siblings (addendum 6 measured the one outline; 91 §3b's and 98 §3's join rules read that
          adjacency); the Dashboard's card holds them inside its content (`dashboardCard`, the const above). */}
      {!dashboardCard && drawers}
    </Fragment>
  );
}
